import { createFileRoute } from "@tanstack/react-router";

import { bindings } from "../../lib/bindings.server";

const MAX_FILE = 25 * 1024 * 1024;
const ALLOWED_EXT = ["xlsx", "xls", "csv", "pdf", "dwg", "dxf", "step", "stp", "zip"];
const FLOWS = ["spare-parts", "custom-manufacturing", "turnkey-project"];

function json(body: unknown, status = 200) {
  return new Response(JSON.stringify(body), {
    status,
    headers: { "content-type": "application/json; charset=utf-8", "cache-control": "no-store" },
  });
}

function makeReference() {
  const d = new Date();
  const stamp = `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, "0")}${String(d.getUTCDate()).padStart(2, "0")}`;
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `RFQ-${stamp}-${rand}`;
}

const str = (v: FormDataEntryValue | null, max = 500) => (typeof v === "string" ? v.trim().slice(0, max) : "");

export const Route = createFileRoute("/api/rfq")({
  server: {
    handlers: {
      POST: async ({ request }) => {
        let form: FormData;
        try {
          form = await request.formData();
        } catch {
          return json({ error: "invalid_form" }, 400);
        }

        const flow = str(form.get("flow"), 40);
        const lang = str(form.get("lang"), 5) || "en";
        const company = str(form.get("company"), 200);
        const contact = str(form.get("contact"), 200);
        const email = str(form.get("email"), 200);
        if (!FLOWS.includes(flow) || !company || !contact || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
          return json({ error: "missing_fields" }, 400);
        }
        if (!str(form.get("consent"), 4)) return json({ error: "consent_required" }, 400);
        // Honeypot: real users never fill it.
        if (str(form.get("website"))) return json({ reference: makeReference() });

        const payload: Record<string, string> = {};
        for (const [key, value] of form.entries()) {
          if (typeof value === "string" && key !== "file" && key !== "website") payload[key] = value.slice(0, 4000);
        }

        const { DB, STORAGE } = bindings();
        const reference = makeReference();

        let fileKey: string | null = null;
        let fileName: string | null = null;
        let fileSize: number | null = null;
        const file = form.get("file");
        if (file instanceof File && file.size > 0) {
          const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
          if (!ALLOWED_EXT.includes(ext)) return json({ error: "file_type" }, 400);
          if (file.size > MAX_FILE) return json({ error: "file_size" }, 400);
          fileName = file.name.slice(0, 200);
          fileSize = file.size;
          if (STORAGE) {
            fileKey = `rfq/${reference}/${fileName.replace(/[^\w.\-]+/g, "_")}`;
            await STORAGE.put(fileKey, await file.arrayBuffer(), {
              httpMetadata: { contentType: file.type || "application/octet-stream" },
              customMetadata: { reference, flow, email },
            });
          }
        }

        if (DB) {
          await DB.prepare(
            `INSERT INTO rfq_requests
              (reference, flow, lang, division, country, company, contact_name, email, phone, preferred_language,
               destination_port, incoterms, consolidation, payload, file_key, file_name, file_size)
             VALUES (?1, ?2, ?3, ?4, ?5, ?6, ?7, ?8, ?9, ?10, ?11, ?12, ?13, ?14, ?15, ?16, ?17)`,
          )
            .bind(
              reference,
              flow,
              lang,
              str(form.get("division"), 80) || null,
              str(form.get("country"), 120) || null,
              company,
              contact,
              email,
              str(form.get("phone"), 60) || null,
              str(form.get("language"), 20) || null,
              str(form.get("port"), 120) || null,
              str(form.get("incoterms"), 10) || null,
              form.get("consolidation") ? 1 : 0,
              JSON.stringify(payload),
              fileKey,
              fileName,
              fileSize,
            )
            .run();
        }

        return json({ reference });
      },
    },
  },
});
